import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";

const BOOKINGS_COLLECTION = "villaInvoices"; // ✅ Correct
const VILLA_COLLECTION = "ClarksExoticaVilla";

const safeString = (v) => String(v || "").toLowerCase().trim();

const safeNum = (v) => {
  const n = typeof v === "string" ? parseFloat(v.toString().replace(/,/g, "")) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatINR = (num) => {
  try {
    return new Intl.NumberFormat("en-IN").format(safeNum(num));
  } catch {
    return safeNum(num).toLocaleString();
  }
};

// ✅ paise -> rupees
const paiseToRupees = (paise) => {
  const p = safeNum(paise);
  return p > 0 ? p / 100 : 0;
};

const statusToClass = (status) => {
  const s = safeString(status);
  if (!s) return "unknown";
  return s.replace(/\s+/g, "-");
};

const parseTimestamp = (timestamp) => {
  // timestamp stored as string ISO "2026-01-31T08:49:38.826Z"
  if (!timestamp) return null;
  const d = new Date(timestamp);
  return isNaN(d.getTime()) ? null : d;
};

const Bookings = ({ setError }) => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [showCloseDealConfirmModal, setShowCloseDealConfirmModal] = useState(false);
  const [closeDealCheckboxes, setCloseDealCheckboxes] = useState({
    documentsShared: false,
    paymentCompleted: false,
    clientNotified: false,
  });

  const [showCancelDealConfirmModal, setShowCancelDealConfirmModal] = useState(false);
  const [cancelDealCheckboxes, setCancelDealCheckboxes] = useState({
    advanceRefunded: false,
    clientIntimated: false,
    documentsReturned: false,
  });

  const [showContactModal, setShowContactModal] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);

  // ✅ Fetch villaInvoices
  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setBookingsError(null);

    try {
      const ref = collection(db, BOOKINGS_COLLECTION);

      // ✅ order by timestamp (string). This matches your docs
      const q = query(ref, orderBy("timestamp", "desc"));
      const snap = await getDocs(q);

      const rows = snap.docs.map((d) => {
        const data = d.data();

        const dt = parseTimestamp(data.timestamp);
        const dateLabel = dt ? dt.toLocaleDateString("en-IN") : "N/A";
        const sortTime = dt ? dt.getTime() : 0;

        return {
          firestoreDocId: d.id, // ✅ Firestore doc id
          ...data,

          // normalize fields for UI
          bookingNumber: data.bookingNumber || "N/A",
          bookingId: data.bookingId || d.id || "N/A",

          villaNumber: data.villaNumber || "N/A",
          villaId: data.villaId || "N/A",
          villaFirestoreId: data.villaFirestoreId || data.villaId || "N/A",

          advanceAmountPaise: data.advanceAmountPaise ?? 0,

          BookingStatus: data.BookingStatus || "N/A", // ex "Booking Failed"
          status: data.status || "N/A",               // ex "Failed"

          timestamp: data.timestamp || null,
          timestampDate: dateLabel,
          timestampSort: sortTime,
        };
      });

      if (!rows.length) {
        setBookingsError("No bookings found in villaInvoices.");
      }

      setBookings(rows);
      setFilteredBookings(rows);
    } catch (err) {
      console.error("Error fetching villaInvoices:", err);
      setError?.("Failed to fetch booking data.");
      setBookingsError("Failed to fetch booking data. Check console for details.");
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleSearch = () => {
    const q = safeString(searchQuery);
    if (!q) {
      setFilteredBookings(bookings);
      setBookingsError(null);
      return;
    }

    const numeric = q.replace(/[^0-9]/g, "");

    const out = bookings.filter((b) => {
      const hay = [
        b.bookingNumber,
        b.bookingId,
        b.villaNumber,
        b.villaId,
        b.villaFirestoreId,
        b.BookingStatus,
        b.status,
        b.timestamp,
      ]
        .map((x) => safeString(x))
        .join(" | ");

      if (hay.includes(q)) return true;
      if (numeric && hay.includes(numeric)) return true;
      return false;
    });

    if (!out.length) setBookingsError(`No bookings found for "${searchQuery}".`);
    else setBookingsError(null);

    setFilteredBookings(out);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setFilteredBookings(bookings);
    setBookingsError(null);
  };

  const openBookingDetailsModal = (booking) => setSelectedBooking(booking);
  const closeBookingDetailsModal = () => setSelectedBooking(null);

  const openCloseDealConfirmModal = () => setShowCloseDealConfirmModal(true);
  const closeCloseDealConfirmModal = () => {
    setShowCloseDealConfirmModal(false);
    setCloseDealCheckboxes({
      documentsShared: false,
      paymentCompleted: false,
      clientNotified: false,
    });
  };

  const openCancelDealConfirmModal = () => setShowCancelDealConfirmModal(true);
  const closeCancelDealConfirmModal = () => {
    setShowCancelDealConfirmModal(false);
    setCancelDealCheckboxes({
      advanceRefunded: false,
      clientIntimated: false,
      documentsReturned: false,
    });
  };

  const handleCheckboxChange = (e, modalType) => {
    const { name, checked } = e.target;
    if (modalType === "closeDeal") {
      setCloseDealCheckboxes((prev) => ({ ...prev, [name]: checked }));
    } else {
      setCancelDealCheckboxes((prev) => ({ ...prev, [name]: checked }));
    }
  };

  // ✅ Update villa status in inventory
  const updateVillaStatus = async (villaFirestoreId, newStatus) => {
    if (!villaFirestoreId || villaFirestoreId === "N/A") return;

    // Try direct doc update first
    try {
      await updateDoc(doc(db, VILLA_COLLECTION, villaFirestoreId), { Status: newStatus });
      return;
    } catch {
      // fallback: find by villaId == "villa-48"
      const villasRef = collection(db, VILLA_COLLECTION);
      const q = query(villasRef, where("villaId", "==", villaFirestoreId));
      const snap = await getDocs(q);

      if (!snap.empty) {
        await updateDoc(doc(db, VILLA_COLLECTION, snap.docs[0].id), { Status: newStatus });
      }
    }
  };

  const confirmCloseDeal = async () => {
    if (!selectedBooking) return;
    try {
      await updateDoc(doc(db, BOOKINGS_COLLECTION, selectedBooking.firestoreDocId), {
        BookingStatus: "Booking Closed",
      });

      await updateVillaStatus(selectedBooking.villaFirestoreId, "Sold");

      const patch = (prev) =>
        prev.map((x) =>
          x.firestoreDocId === selectedBooking.firestoreDocId
            ? { ...x, BookingStatus: "Booking Closed" }
            : x
        );

      setBookings(patch);
      setFilteredBookings(patch);

      closeCloseDealConfirmModal();
      closeBookingDetailsModal();
      setError?.("Deal closed successfully!");
    } catch (err) {
      console.error("Error closing deal:", err);
      setError?.("Failed to close deal.");
      setBookingsError("Failed to close deal.");
    }
  };

  const confirmCancelDeal = async () => {
    if (!selectedBooking) return;
    try {
      await updateDoc(doc(db, BOOKINGS_COLLECTION, selectedBooking.firestoreDocId), {
        BookingStatus: "Booking Cancelled",
      });

      await updateVillaStatus(selectedBooking.villaFirestoreId, "Available");

      const patch = (prev) =>
        prev.map((x) =>
          x.firestoreDocId === selectedBooking.firestoreDocId
            ? { ...x, BookingStatus: "Booking Cancelled" }
            : x
        );

      setBookings(patch);
      setFilteredBookings(patch);

      closeCancelDealConfirmModal();
      closeBookingDetailsModal();
      setError?.("Deal cancelled successfully!");
    } catch (err) {
      console.error("Error cancelling deal:", err);
      setError?.("Failed to cancel deal.");
      setBookingsError("Failed to cancel deal.");
    }
  };

  const openContactModal = () => setShowContactModal(true);
  const closeContactModal = () => setShowContactModal(false);

  const openMailModal = () => setShowMailModal(true);
  const closeMailModal = () => setShowMailModal(false);

  return (
    <div className="md-section-content">
      <div className="md-section-header">
        <h2 className="md-section-title">Bookings</h2>
        <button className="md-close-tab-button" onClick={clearSearch}>
          Close
        </button>
      </div>

      <div className="md-search-container">
        <input
          type="text"
          placeholder="Search by booking number / villa / status"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="md-search-input"
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <button className="md-search-button" onClick={handleSearch} disabled={!searchQuery}>
          Search
        </button>
        <button className="md-clear-button" onClick={clearSearch} disabled={!searchQuery}>
          Clear
        </button>
      </div>

      {loading ? (
        <div className="md-loading">Loading...</div>
      ) : bookingsError ? (
        <div className="md-error">{bookingsError}</div>
      ) : filteredBookings.length === 0 ? (
        <p className="md-no-results">No bookings found.</p>
      ) : (
        <div className="md-users-table-container md-scrollable-table">
          <table className="md-users-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Booking Number</th>
                <th>Villa</th>
                <th>Advance</th>
                <th>Status</th>
                <th>Booking Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((b) => (
                <tr
                  key={b.firestoreDocId || b.bookingId}
                  className="md-user-row"
                  onClick={() => openBookingDetailsModal(b)}
                >
                  <td>{b.timestampDate}</td>
                  <td>{b.bookingNumber}</td>
                  <td>{b.villaNumber || b.villaId}</td>
                  <td>
                    {b.advanceAmountPaise
                      ? `₹ ${formatINR(paiseToRupees(b.advanceAmountPaise))}`
                      : "N/A"}
                  </td>
                  <td>
                    <span className={`md-booking-status ${statusToClass(b.status)}`}>
                      {b.status}
                    </span>
                  </td>
                  <td>
                    <span className={`md-booking-status ${statusToClass(b.BookingStatus)}`}>
                      {b.BookingStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Details Modal */}
      {selectedBooking && (
        <div className="md-modal-overlay">
          <div className="md-modal-content md-invoice-details-modal">
            <div className="md-invoice-details-header">
              <h3 className="md-modal-titles">Booking Details</h3>
              <div className="md-header-right">
                <span className="md-plot-number">{selectedBooking.villaNumber || "N/A"}</span>
                <button className="md-modal-close-button" onClick={closeBookingDetailsModal}>
                  ✕
                </button>
              </div>
            </div>

            <div className="md-invoice-details-content">
              <div className="md-invoice-details-column">
                <p><strong>Date:</strong> {selectedBooking.timestampDate}</p>
                <p><strong>Booking Number:</strong> {selectedBooking.bookingNumber}</p>
                <p><strong>Payment Status:</strong> {selectedBooking.status}</p>
                <p><strong>Booking Status:</strong> {selectedBooking.BookingStatus}</p>
              </div>

              <div className="md-invoice-details-column">
                <p><strong>Villa:</strong> {selectedBooking.villaNumber}</p>
                <p><strong>Villa ID:</strong> {selectedBooking.villaId}</p>
                <p><strong>Advance:</strong>{" "}
                  {selectedBooking.advanceAmountPaise
                    ? `₹ ${formatINR(paiseToRupees(selectedBooking.advanceAmountPaise))}`
                    : "N/A"}
                </p>
              </div>
            </div>

            <div className="md-modal-actions">
              <button className="md-modal-button md-close-deal" onClick={openCloseDealConfirmModal}>
                Close Deal
              </button>
              <button className="md-modal-button md-cancel-deal" onClick={openCancelDealConfirmModal}>
                Deal Cancelled
              </button>
              <button className="md-modal-button md-contact" onClick={openContactModal}>
                Contact
              </button>
              <button className="md-modal-button md-mail" onClick={openMailModal}>
                Mail
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Deal Confirm */}
      {showCloseDealConfirmModal && (
        <div className="md-modal-overlay">
          <div className="md-modal-content">
            <h3 className="md-modal-titles">Confirm Close Deal</h3>

            <div className="md-modal-section">
              <label className="md-checkbox-label">
                <input
                  type="checkbox"
                  name="documentsShared"
                  checked={closeDealCheckboxes.documentsShared}
                  onChange={(e) => handleCheckboxChange(e, "closeDeal")}
                />
                Documents Shared
              </label>

              <label className="md-checkbox-label">
                <input
                  type="checkbox"
                  name="paymentCompleted"
                  checked={closeDealCheckboxes.paymentCompleted}
                  onChange={(e) => handleCheckboxChange(e, "closeDeal")}
                />
                Payment Completed
              </label>

              <label className="md-checkbox-label">
                <input
                  type="checkbox"
                  name="clientNotified"
                  checked={closeDealCheckboxes.clientNotified}
                  onChange={(e) => handleCheckboxChange(e, "closeDeal")}
                />
                Client Notified
              </label>
            </div>

            <div className="md-modal-actions">
              <button
                className="md-modal-button md-save"
                onClick={confirmCloseDeal}
                disabled={
                  !closeDealCheckboxes.documentsShared ||
                  !closeDealCheckboxes.paymentCompleted ||
                  !closeDealCheckboxes.clientNotified
                }
              >
                Yes, Deal Was Closed
              </button>
              <button className="md-modal-button md-cancel" onClick={closeCloseDealConfirmModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Deal Confirm */}
      {showCancelDealConfirmModal && (
        <div className="md-modal-overlay">
          <div className="md-modal-content">
            <h3 className="md-modal-titles">Confirm Cancel Deal</h3>

            <div className="md-modal-section">
              <label className="md-checkbox-label">
                <input
                  type="checkbox"
                  name="advanceRefunded"
                  checked={cancelDealCheckboxes.advanceRefunded}
                  onChange={(e) => handleCheckboxChange(e, "cancelDeal")}
                />
                Advance Amount Refunded
              </label>

              <label className="md-checkbox-label">
                <input
                  type="checkbox"
                  name="clientIntimated"
                  checked={cancelDealCheckboxes.clientIntimated}
                  onChange={(e) => handleCheckboxChange(e, "cancelDeal")}
                />
                Client Intimated
              </label>

              <label className="md-checkbox-label">
                <input
                  type="checkbox"
                  name="documentsReturned"
                  checked={cancelDealCheckboxes.documentsReturned}
                  onChange={(e) => handleCheckboxChange(e, "cancelDeal")}
                />
                Documents Returned
              </label>
            </div>

            <div className="md-modal-actions">
              <button
                className="md-modal-button md-save"
                onClick={confirmCancelDeal}
                disabled={
                  !cancelDealCheckboxes.advanceRefunded ||
                  !cancelDealCheckboxes.clientIntimated ||
                  !cancelDealCheckboxes.documentsReturned
                }
              >
                Yes, Cancel Deal
              </button>
              <button className="md-modal-button md-cancel" onClick={closeCancelDealConfirmModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="md-modal-overlay">
          <div className="md-modal-content">
            <h3 className="md-modal-titles">Contact Information</h3>
            <p className="md-modal-text">
              This invoice schema doesn’t include mobile/email in your example.
            </p>
            <div className="md-modal-actions">
              <button className="md-modal-button md-cancel" onClick={closeContactModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mail Modal */}
      {showMailModal && (
        <div className="md-modal-overlay">
          <div className="md-modal-content">
            <h3 className="md-modal-titles">Email Information</h3>
            <p className="md-modal-text">
              This invoice schema doesn’t include mobile/email in your example.
            </p>
            <div className="md-modal-actions">
              <button className="md-modal-button md-cancel" onClick={closeMailModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
